<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-card-container anna-group">
    <div class="anna-lt-head">
      <oxd-text tag="h6" class="orangehrm-main-title"
        >Letter templates</oxd-text
      >
      <oxd-button
        v-if="!draft"
        display-type="secondary"
        icon-name="plus"
        label="New letter"
        @click="startNew"
      />
    </div>
    <oxd-text tag="p" class="anna-purpose">
      The letters HR emails candidates from their Documents tab: job offers,
      rejections, welcome letters. Each is edited there for the candidate before
      it goes; here the template itself changes. A job offer letter makes the
      candidate Job Offered, a rejection Rejected. Every save is kept under
      "Earlier versions".
    </oxd-text>

    <template v-if="!draft">
      <p v-if="loading" class="anna-lt-muted">Loading…</p>
      <p v-else-if="!templates.length" class="anna-lt-muted">No letters yet.</p>
      <div v-for="t in templates" :key="t.id" class="anna-lt-row">
        <span class="anna-lt-kind">{{ kinds[t.kind] || t.kind }}</span>
        <span class="anna-lt-name">{{ t.title }}</span>
        <span class="anna-lt-muted">
          saved {{ (t.savedAt || '').slice(0, 16)
          }}{{ t.savedBy ? ` by ${t.savedBy}` : '' }}
        </span>
        <oxd-button
          display-type="ghost"
          size="medium"
          label="Edit"
          @click="edit(t.id)"
        />
      </div>
    </template>

    <template v-else>
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field v-model="draft.title" label="Name" required />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="draft.kind"
            type="select"
            label="Kind"
            :options="kindOptions"
          />
        </oxd-grid-item>
        <oxd-grid-item v-if="draft.id && draft.versions.length > 1">
          <oxd-input-field
            v-model="draft.version"
            type="select"
            label="Earlier versions"
            :options="versionOptions"
            @update:model-value="loadVersion"
          />
        </oxd-grid-item>
        <oxd-grid-item v-else-if="!draft.id">
          <oxd-input-field
            v-model="draft.from"
            type="select"
            label="Start from"
            :options="fromOptions"
            @update:model-value="startFrom"
          />
        </oxd-grid-item>
      </oxd-grid>
      <oxd-input-field v-model="draft.subject" label="Email subject" required />
      <p class="anna-lt-muted">
        Click into the letter to change it. Bold, italic and the like come with
        the usual shortcuts (Ctrl+B, Ctrl+I); pasting from another email keeps
        its look.
      </p>
      <letter-editor ref="editor" :key="draft.rev" :html="draft.html" />
      <div class="anna-lt-actions">
        <oxd-button
          v-if="draft.id"
          display-type="label-danger"
          label="Delete"
          :disabled="busy"
          @click="remove"
        />
        <span class="anna-lt-spacer"></span>
        <oxd-button
          display-type="ghost"
          label="Close"
          :disabled="busy"
          @click="draft = null"
        />
        <oxd-button
          display-type="secondary"
          :label="busy ? 'Saving…' : 'Save'"
          :disabled="busy || !draft.title.trim() || !draft.subject.trim()"
          @click="save"
        />
      </div>
    </template>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import LetterEditor from './LetterEditor.vue';

const BLANK =
  '<!DOCTYPE html><html><head><meta charset="utf-8"></head>' +
  '<body style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333;">' +
  '<p>Dear Name,</p><p><br></p></body></html>';

// ANNA Settings → Letters: the letter templates, each save a new version.
export default {
  name: 'LetterTemplates',
  components: {'letter-editor': LetterEditor},
  data() {
    return {loading: true, busy: false, templates: [], kinds: {}, draft: null};
  },
  computed: {
    kindOptions() {
      return Object.entries(this.kinds).map(([id, label]) => ({id, label}));
    },
    versionOptions() {
      return (this.draft?.versions || []).map((v, i) => ({
        id: v.id,
        label: `${(v.savedAt || '').slice(0, 16)}${
          v.savedBy ? ` · ${v.savedBy}` : ''
        }${v.note ? ` · ${v.note}` : ''}${i === 0 ? ' (current)' : ''}`,
      }));
    },
    fromOptions() {
      return [
        {id: 0, label: 'A blank letter'},
        ...this.templates.map((t) => ({
          id: t.id,
          label: `A copy of ${t.title}`,
        })),
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    api(path = '') {
      return new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/anna/letter-templates${path}`,
      );
    },
    load() {
      this.loading = true;
      return this.api()
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          this.templates = data.templates || [];
          this.kinds = data.kinds || {};
        })
        .catch(() => null)
        .finally(() => {
          this.loading = false;
        });
    },
    kindOption(kind) {
      return this.kindOptions.find((o) => o.id === kind) || null;
    },
    startNew() {
      this.draft = {
        id: null,
        title: '',
        kind: this.kindOption('offer'),
        subject: '',
        html: BLANK,
        versions: [],
        from: this.fromOptions[0],
        version: null,
        rev: Date.now(),
      };
    },
    startFrom(option) {
      const t = this.templates.find((x) => x.id === option?.id);
      Object.assign(this.draft, {
        html: t ? t.html : BLANK,
        subject: t ? t.subject : this.draft.subject,
        kind: t ? this.kindOption(t.kind) : this.draft.kind,
        rev: Date.now(),
      });
    },
    edit(id) {
      this.busy = true;
      this.api(`/${id}`)
        .request({method: 'GET'})
        .then(({data: {data}}) => this.fill(data))
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
    fill(t) {
      this.draft = {
        id: t.id,
        title: t.title,
        kind: this.kindOption(t.kind),
        subject: t.subject,
        html: t.html,
        versions: t.versions || [],
        version: null,
        from: null,
        rev: Date.now(),
      };
      this.draft.version = this.versionOptions[0] || null;
    },
    // an earlier version into the editor; saving makes it the current one again
    loadVersion(option) {
      const v = this.draft.versions.find((x) => x.id === option?.id);
      if (v)
        Object.assign(this.draft, {
          html: v.html,
          subject: v.subject,
          rev: Date.now(),
        });
    },
    save() {
      const html = this.$refs.editor?.getHtml();
      if (!html) return;
      this.busy = true;
      this.api(this.draft.id ? `/${this.draft.id}` : '')
        .request({
          method: 'PUT',
          data: {
            title: this.draft.title,
            kind: this.draft.kind?.id || 'other',
            subject: this.draft.subject,
            html,
          },
        })
        .then(({data: {data}}) => {
          this.$toast.saveSuccess();
          this.fill(data);
          return this.load();
        })
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
    remove() {
      if (
        !window.confirm(
          `Delete the letter "${this.draft.title}"? Letters already sent with it stay as they were.`,
        )
      )
        return;
      this.busy = true;
      this.api(`/${this.draft.id}`)
        .request({method: 'PUT', data: {delete: true}})
        .then(() => {
          this.$toast.deleteSuccess();
          this.draft = null;
          return this.load();
        })
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-lt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.anna-lt-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f2f5;
}
.anna-lt-kind {
  min-width: 80px;
  font-size: 12px;
  font-weight: 700;
  color: #64728c;
}
.anna-lt-name {
  font-size: 13px;
  font-weight: 700;
}
.anna-lt-muted {
  margin: 0;
  font-size: 12px;
  color: #64728c;
}
p.anna-lt-muted {
  margin: 8px 0;
}
.anna-lt-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.anna-lt-spacer {
  flex: 1;
}
</style>
